import * as React from 'react';
import * as mobx from 'mobx';
import { isBefore } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { MEDIUM_PLAIN_TEXT_2000_LENGTH, SHORT_PLAIN_TEXT_200_LENGTH } from '../../../../../../constants/common.constants';
import { IZoomMeetingDTO, ZoomMeetingType } from '../../../../../../api/zoom.dao';
import { MILLISECONDS_IN_DAY } from '../../../../../../constants/date-time.constants';
import { utcToZonedTime } from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { WebinarAutoRecording } from '../../../../../../api/connections.dao';

export enum ApprovalType {
    AUTOMATICALLY,
    MANUALLY,
    NOT_REQUIRED
}

class CreateZoomWebinarFormDataVM {
    @mobx.observable
    private formChecked: boolean;
    @mobx.observable
    private timeZone: string;
    @mobx.observable
    private selectedGroupId: number;
    @mobx.observable
    private agenda: string;
    @mobx.observable
    private meetingTopic: string;
    @mobx.observable
    private startDateTime: Date;
    @mobx.observable
    private endDateTime: Date;
    @mobx.observable
    public validationErrors: Record<string, JSX.Element>;
    @mobx.observable
    public registrationEnabled: boolean;
    @mobx.observable
    public approvalType: ApprovalType;
    @mobx.observable
    public registrantsConfirmationEmail: boolean = true;
    @mobx.observable
    public autoRegistrationEnabled: boolean = false;
    @mobx.observable
    public syncMeetingAttendeesWithZoomEnabled: boolean = false;
    @mobx.observable
    public autoRecording: WebinarAutoRecording = WebinarAutoRecording.Cloud;

    constructor(
        private readonly eventTimeZone: string,
        private readonly zoomMeetingTopic: string,
        private readonly meetingStartDateTime: Date,
        private readonly meetingEndDateTime: Date
    ) {
        this.cleanup();
    }


    public getStartMinDate = () => {
        return utcToZonedTime(new Date(), this.timeZone);
    };

    public getStartDateTime = () => {
        return this.startDateTime;
    };

    @mobx.action
    public setStartDateTime = (startDateTime: Date) => {
        this.startDateTime = startDateTime;
        this.checkDates();
    };

    public getEndDateTime = () => {
        return this.endDateTime;
    };

    @mobx.action
    public setEndDateTime = (endDateTime: Date) => {
        this.endDateTime = endDateTime;
        this.checkDates();
    };

    public getGroup = () => {
        return this.selectedGroupId;
    };

    @mobx.action
    public setGroup = (groupId: number) => {
        this.selectedGroupId = groupId;
    };

    public getTimeZone = () => {
        return this.timeZone;
    };

    @mobx.action
    public setTimeZone = (timeZone: string) => {
        this.timeZone = timeZone;
    };

    public getMeetingTopic = () => {
        return this.meetingTopic;
    };

    public getDescription = (): string => {
        return this.agenda;
    };

    @mobx.action
    public setDescription = (value: string) => {
        this.agenda = value;
        this.checkDescription();
    };

    @mobx.action
    public setMeetingTopic = (meetingTopic: string) => {
        this.meetingTopic = meetingTopic;
    };

    public getFieldError = (fieldName: string): JSX.Element | undefined => {
        return this.validationErrors[fieldName];
    };

    @mobx.action
    public checkForm(): boolean {
        this.checkMeetingTopic();
        this.checkDates();
        return !this.validationErrors.meetingTopic && !this.validationErrors.dates && !this.validationErrors.description;
    }

    @mobx.action
    public getAPIDTO = (): IZoomMeetingDTO => {
        const dataToDTO: IZoomMeetingDTO = {
            topic: this.meetingTopic,
            zoomGroupId: this.selectedGroupId,
            timeZone: this.timeZone,
            endTime: this.endDateTime,
            startTime: this.startDateTime,
            agenda: this.agenda,
            settings: {
                registrantsConfirmationEmail: this.registrantsConfirmationEmail,
                approvalType: this.registrationEnabled ? this.approvalType : ApprovalType.NOT_REQUIRED,
                autoRegistrationEnabled: this.autoRegistrationEnabled,
                autoSyncEnabled: this.syncMeetingAttendeesWithZoomEnabled,
                autoRecording: this.autoRecording
            },
            type: ZoomMeetingType.WEBINAR
        };
        return dataToDTO;
    };

    @mobx.action
    public cleanup = () => {
        this.formChecked = false;
        this.timeZone = this.eventTimeZone;
        this.selectedGroupId = null;
        this.meetingTopic = this.zoomMeetingTopic;
        this.startDateTime = this.meetingStartDateTime || null;
        this.endDateTime = this.meetingEndDateTime || null;
        this.agenda = null;
        this.validationErrors = {};
        this.registrationEnabled = true;
        this.autoRegistrationEnabled = false;
        this.approvalType = ApprovalType.AUTOMATICALLY;
    };

    @mobx.action
    public checkDescription(): void {
        if (this.validationErrors.description)
            delete this.validationErrors.description;
        if (this.agenda.length > MEDIUM_PLAIN_TEXT_2000_LENGTH) {
            this.validationErrors.description = <FormattedMessage
                id='oe.attendee.MaxLengthIs'
                description='Error label text'
                defaultMessage='Max length is {length} characters'
                values={{
                    length: MEDIUM_PLAIN_TEXT_2000_LENGTH
                }}
            />;
            return;
        }
    }

    @mobx.action
    public checkMeetingTopic(): void {
        if (this.validationErrors.meetingTopic)
            delete this.validationErrors.meetingTopic;
        if (this.meetingTopic) {
            this.meetingTopic = this.meetingTopic.trim();
        }
        if (!this.meetingTopic) {
            this.validationErrors.meetingTopic = <FormattedMessage
                defaultMessage='This field is required'
                id='oe.error.required'
                description='Error message text'
            />;
        }
        if (this.meetingTopic.length > SHORT_PLAIN_TEXT_200_LENGTH) {
            this.validationErrors.meetingTopic = <FormattedMessage
                id='oe.attendee.MaxLengthIs'
                description='Error label text'
                defaultMessage='Max length is {length} characters'
                values={{
                    length: SHORT_PLAIN_TEXT_200_LENGTH
                }}
            />;
            return;
        }
    }

    @mobx.action
    public checkDates() {
        if (this.validationErrors.dates)
            delete this.validationErrors.dates;

        if (!this.startDateTime || !this.endDateTime) {
            this.validationErrors.dates = <FormattedMessage
                id='oe.startEndTime.required'
                description='Error label text'
                defaultMessage='Start Time and End Time are required fields!'
            />;
            return;
        }
        if (!isBefore(this.startDateTime, this.endDateTime)) {
            this.validationErrors.dates = <FormattedMessage
                id='oe.startEndTime.cannotBeEqualOrGreater'
                description='Error label text'
                defaultMessage='Start time cannot be equal or greater than End time'
            />;
            return;
        }
        if (isBefore(this.startDateTime, this.getStartMinDate())) {
            this.validationErrors.dates = <FormattedMessage
                id='oe.startEndTime.cannotBeInPast'
                description='Error label text'
                defaultMessage='Start time cannot be in the past'
            />;
            return;
        }
        if ((this.endDateTime.getTime() - this.startDateTime.getTime()) > MILLISECONDS_IN_DAY) {
            this.validationErrors.dates = <FormattedMessage
                id='oe.zoomWebinar.longerThenDay'
                description='Error label text'
                defaultMessage='Zoom Webinar cannot be longer than 24 hours'
            />;
            return;
        }
    }

    @mobx.action
    public toggleRegistration = (): void => {
        this.registrationEnabled = !this.registrationEnabled;
    };

    @mobx.action
    public toggleEmailConfirmation = (): void => {
        this.registrantsConfirmationEmail = !this.registrantsConfirmationEmail;
    };

    @mobx.action
    public togglePassportAutoRegistration = (): void => {
        this.autoRegistrationEnabled = !this.autoRegistrationEnabled;
        this.checkEmailConfirmation();

        if (this.autoRegistrationEnabled) this.enableSyncMeetingAttendeesWithZoom();
    };

    @mobx.action
    public checkEmailConfirmation = (): void => {
        if (this.autoRegistrationEnabled && this.registrantsConfirmationEmail) {
            this.toggleEmailConfirmation();
        }
    };

    @mobx.action
    public toggleapprovalType = (): void => {
        if (this.approvalType === ApprovalType.AUTOMATICALLY) {
            this.approvalType = ApprovalType.MANUALLY;
        } else {
            this.approvalType = ApprovalType.AUTOMATICALLY;
        }
    };

    @mobx.action.bound
    public toggleSyncMeetingAttendees(): void {
        if (!this.syncMeetingAttendeesWithZoomEnabled) {
            this.enableSyncMeetingAttendeesWithZoom();
        } else {
            this.disableSyncMeetingAttendeesWithZoom();
        }
    }

    @mobx.action.bound
    public toggleAutoRecording(): void {
        if (this.autoRecording === WebinarAutoRecording.Cloud) {
            this.autoRecording = WebinarAutoRecording.None;
        } else {
            this.autoRecording = WebinarAutoRecording.Cloud;
        }
    }

    @mobx.action
    private enableSyncMeetingAttendeesWithZoom(): void {
        this.syncMeetingAttendeesWithZoomEnabled = true;
        this.registrationEnabled = true;
        this.approvalType = ApprovalType.AUTOMATICALLY;
        this.registrantsConfirmationEmail = false;
    }
    @mobx.action
    private disableSyncMeetingAttendeesWithZoom(): void {
        this.syncMeetingAttendeesWithZoomEnabled = false;
    }
}

export { CreateZoomWebinarFormDataVM };
